/**
 * Registersider for tenant-admin: personer, økonomigrupper, leverandørkontoer,
 * anlegg, nettleie, strømavtaler og Zaptec-brukermapping.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  fmtDato,
  fmtTid,
  type EconomyGroup,
  type GridTariff,
  type Installation,
  type Person,
  type PowerDeal,
  type VendorAccount,
} from '../lib/api.ts';
import { Field, inputKlasse, knappPrimær, knappSekundær, Modal } from '../components/ui.tsx';
import { KontoRedigerModal, KontoStatus } from '../components/konto.tsx';

const TABS = [
  'Personer',
  'Økonomigrupper',
  'Leverandørkontoer',
  'Anlegg',
  'Nettleie',
  'Strømavtaler',
  'Zaptec-brukere',
] as const;

export default function Register() {
  const [tab, setTab] = useState<(typeof TABS)[number]>('Personer');

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-slate-800">Register</h1>
      <div className="flex flex-wrap gap-1">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-lg px-3 py-1.5 text-sm font-medium ${
              tab === t ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 ring-1 ring-slate-200'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Personer' && <Personer />}
      {tab === 'Økonomigrupper' && <Grupper />}
      {tab === 'Leverandørkontoer' && <Kontoer />}
      {tab === 'Anlegg' && <Anlegg />}
      {tab === 'Nettleie' && <Nettleie />}
      {tab === 'Strømavtaler' && <Avtaler />}
      {tab === 'Zaptec-brukere' && <ZaptecBrukere />}
    </div>
  );
}

function Tabell({ hoder, children }: { hoder: string[]; children: ReactNode }) {
  return (
    <div className="overflow-x-auto rounded-xl bg-white ring-1 ring-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-400">
            {hoder.map((h) => (
              <th key={h} className="px-4 py-3">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// ---------- Personer ----------

function Personer() {
  const qc = useQueryClient();
  const [rediger, setRediger] = useState<Person | 'ny' | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const personsQuery = useQuery<Person[]>({ queryKey: ['persons'], queryFn: () => api.get('/persons') });
  const grupperQuery = useQuery<EconomyGroup[]>({
    queryKey: ['economy-groups'],
    queryFn: () => api.get('/economy-groups'),
  });
  const grupper = grupperQuery.data ?? [];
  const gruppeNavn = (id: string | null) => grupper.find((g) => g.id === id)?.navn ?? '–';

  const fjern = useMutation({
    mutationFn: (personId: string) => api.delete(`/persons/${personId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['persons'] });
      setFeil(null);
    },
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Kunne ikke fjerne bruker'),
  });

  return (
    <div className="space-y-3">
      <button onClick={() => setRediger('ny')} className={knappPrimær}>
        Ny person
      </button>
      {feil && <p className="text-sm text-red-600">{feil}</p>}
      <Tabell hoder={['Navn', 'E-post', 'Rolle', 'Økonomigruppe', 'Aktiv', '']}>
        {(personsQuery.data ?? []).map((p) => (
          <tr key={p.id} className="border-b border-slate-100 last:border-0">
            <td className="px-4 py-2 font-medium text-slate-700">{p.navn}</td>
            <td className="px-4 py-2">{p.epost}</td>
            <td className="px-4 py-2">{p.rolle}</td>
            <td className="px-4 py-2">{gruppeNavn(p.economyGroupId)}</td>
            <td className="px-4 py-2">{p.aktiv ? 'Ja' : 'Nei'}</td>
            <td className="px-4 py-2 text-right">
              <button onClick={() => setRediger(p)} className="mr-3 text-xs text-blue-600 hover:underline">
                Rediger
              </button>
              <button
                onClick={() => fjern.mutate(p.id)}
                disabled={fjern.isPending}
                className="text-xs text-red-600 hover:underline"
              >
                Fjern
              </button>
            </td>
          </tr>
        ))}
      </Tabell>

      {rediger && (
        <PersonModal
          person={rediger === 'ny' ? null : rediger}
          grupper={grupper}
          onClose={() => {
            setRediger(null);
            qc.invalidateQueries({ queryKey: ['persons'] });
          }}
        />
      )}
    </div>
  );
}

function PersonModal({
  person,
  grupper,
  onClose,
}: {
  person: Person | null;
  grupper: EconomyGroup[];
  onClose: () => void;
}) {
  const [feil, setFeil] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const body: Record<string, unknown> = {
      navn: fd.get('navn'),
      epost: fd.get('epost'),
      telefon: fd.get('telefon') || null,
      rolle: fd.get('rolle'),
      economyGroupId: fd.get('gruppe') || null,
    };
    const passord = fd.get('passord') as string;
    if (passord) body.passord = passord;
    if (person) body.aktiv = fd.get('aktiv') === 'ja';
    try {
      if (person) await api.patch(`/persons/${person.id}`, body);
      else await api.post('/persons', body);
      onClose();
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    }
  }

  return (
    <Modal tittel={person ? 'Rediger person' : 'Ny person'} onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Navn">
          <input name="navn" required defaultValue={person?.navn} className={inputKlasse} />
        </Field>
        <Field label="E-post (brukes ved innlogging)">
          <input name="epost" type="email" required defaultValue={person?.epost} className={inputKlasse} />
        </Field>
        <Field label="Telefon">
          <input name="telefon" defaultValue={person?.telefon ?? ''} className={inputKlasse} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Rolle">
            <select name="rolle" defaultValue={person?.rolle ?? 'bruker'} className={inputKlasse}>
              <option value="bruker">Bruker</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <Field label="Økonomigruppe">
            <select name="gruppe" defaultValue={person?.economyGroupId ?? ''} className={inputKlasse}>
              <option value="">– ingen –</option>
              {grupper.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.navn}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label={person ? 'Nytt passord (tomt = uendret)' : 'Passord (min. 8 tegn)'}>
          <input name="passord" type="password" className={inputKlasse} />
        </Field>
        {!person && (
          <p className="text-xs text-slate-400">
            Finnes e-posten fra før (f.eks. medlem av en annen tenant), gjenbrukes brukeren og
            passordfeltet kan stå tomt.
          </p>
        )}
        {person && (
          <Field label="Aktiv">
            <select name="aktiv" defaultValue={person.aktiv ? 'ja' : 'nei'} className={inputKlasse}>
              <option value="ja">Ja</option>
              <option value="nei">Nei</option>
            </select>
          </Field>
        )}
        {feil && <p className="text-sm text-red-600">{feil}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={knappSekundær}>
            Avbryt
          </button>
          <button type="submit" className={knappPrimær}>
            Lagre
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Økonomigrupper ----------

function Grupper() {
  const qc = useQueryClient();
  const grupperQuery = useQuery<EconomyGroup[]>({
    queryKey: ['economy-groups'],
    queryFn: () => api.get('/economy-groups'),
  });
  const [navn, setNavn] = useState('');
  const opprett = useMutation({
    mutationFn: () => api.post('/economy-groups', { navn }),
    onSuccess: () => {
      setNavn('');
      qc.invalidateQueries({ queryKey: ['economy-groups'] });
    },
  });

  return (
    <div className="space-y-3">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (navn.trim()) opprett.mutate();
        }}
        className="flex items-end gap-2"
      >
        <Field label="Ny økonomigruppe">
          <input value={navn} onChange={(e) => setNavn(e.target.value)} className={inputKlasse} />
        </Field>
        <button type="submit" className={knappPrimær} disabled={!navn.trim() || opprett.isPending}>
          Opprett
        </button>
      </form>
      <Tabell hoder={['Navn']}>
        {(grupperQuery.data ?? []).map((g) => (
          <tr key={g.id} className="border-b border-slate-100 last:border-0">
            <td className="px-4 py-2">{g.navn}</td>
          </tr>
        ))}
      </Tabell>
      <p className="text-xs text-slate-400">
        Flere personer med samme økonomigruppe summeres sammen i økonomibildet.
      </p>
    </div>
  );
}

// ---------- Leverandørkontoer ----------

function Kontoer() {
  const qc = useQueryClient();
  const [nyKonto, setNyKonto] = useState(false);
  const [redigerKonto, setRedigerKonto] = useState<VendorAccount | null>(null);
  const [feil, setFeil] = useState<string | null>(null);
  const [melding, setMelding] = useState<string | null>(null);
  const kontoerQuery = useQuery<VendorAccount[]>({
    queryKey: ['vendor-accounts'],
    queryFn: () => api.get('/vendor-accounts'),
    refetchInterval: 30_000,
  });

  const synk = useMutation({
    mutationFn: (id: string) => api.post(`/vendor-accounts/${id}/sync`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vendor-accounts'] });
      qc.invalidateQueries({ queryKey: ['chargers'] });
      qc.invalidateQueries({ queryKey: ['installations'] });
    },
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Synk feilet'),
  });

  const fjern = useMutation({
    mutationFn: (id: string) => api.delete<{ anlegg: number; ladere: number; okter: number }>(
      `/vendor-accounts/${id}`,
    ),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['vendor-accounts'] });
      qc.invalidateQueries({ queryKey: ['chargers'] });
      qc.invalidateQueries({ queryKey: ['installations'] });
      setFeil(null);
      setMelding(`Fjernet: ${r.anlegg} anlegg, ${r.ladere} ladere, ${r.okter} økter.`);
    },
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Kunne ikke fjerne kontoen'),
  });

  function bekreftFjern(k: VendorAccount) {
    const svar = window.prompt(
      `Fjerner kontoen «${k.navn}» og ALT som hører til: anlegg, ladere og ladeøkter. ` +
        `Dette kan ikke angres.\n\nSkriv kontonavnet for å bekrefte:`,
    );
    if (svar === k.navn) fjern.mutate(k.id);
    else if (svar !== null) setFeil('Navnet stemte ikke — ingenting ble fjernet.');
  }

  return (
    <div className="space-y-3">
      <button onClick={() => setNyKonto(true)} className={knappPrimær}>
        Ny Zaptec-konto
      </button>
      {feil && <p className="text-sm text-red-600">{feil}</p>}
      {melding && <p className="text-sm text-slate-600">{melding}</p>}
      <Tabell hoder={['Navn', 'Leverandør', 'Brukernavn', 'Kontakt', 'Sist synket', '']}>
        {(kontoerQuery.data ?? []).map((k) => (
          <tr key={k.id} className="border-b border-slate-100 last:border-0">
            <td className="px-4 py-2 font-medium text-slate-700">{k.navn}</td>
            <td className="px-4 py-2 capitalize">{k.vendor}</td>
            <td className="px-4 py-2">{k.username ?? '–'}</td>
            <td className="px-4 py-2">
              <KontoStatus status={k.status} feil={k.sisteFeil} />
            </td>
            <td className="px-4 py-2">{fmtTid(k.sisteSynk)}</td>
            <td className="px-4 py-2 text-right">
              <button
                onClick={() => synk.mutate(k.id)}
                disabled={synk.isPending}
                className="mr-3 text-xs text-blue-600 hover:underline"
              >
                Synk nå
              </button>
              <button
                onClick={() => setRedigerKonto(k)}
                className="mr-3 text-xs text-blue-600 hover:underline"
              >
                Rediger
              </button>
              <button
                onClick={() => bekreftFjern(k)}
                disabled={fjern.isPending}
                className="text-xs text-red-600 hover:underline"
              >
                Fjern
              </button>
            </td>
          </tr>
        ))}
      </Tabell>
      <p className="text-xs text-slate-400">
        Zaptec-kontoen er innloggingen til Zaptec Portal. Anlegg og ladere hentes automatisk etter registrering.
      </p>

      {nyKonto && (
        <KontoModal
          onClose={() => {
            setNyKonto(false);
            qc.invalidateQueries({ queryKey: ['vendor-accounts'] });
          }}
        />
      )}
      {redigerKonto && (
        <KontoRedigerModal
          konto={redigerKonto}
          onClose={() => {
            setRedigerKonto(null);
            qc.invalidateQueries({ queryKey: ['vendor-accounts'] });
          }}
        />
      )}
    </div>
  );
}

function KontoModal({ onClose }: { onClose: () => void }) {
  const [feil, setFeil] = useState<string | null>(null);
  const [jobber, setJobber] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setJobber(true);
    setFeil(null);
    const fd = new FormData(e.currentTarget);
    try {
      await api.post('/vendor-accounts', {
        vendor: 'zaptec',
        navn: fd.get('navn'),
        username: fd.get('username'),
        password: fd.get('password'),
      });
      onClose();
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    } finally {
      setJobber(false);
    }
  }

  return (
    <Modal tittel="Ny Zaptec-konto" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Visningsnavn (f.eks. «Hjemme» eller «Borettslaget»)">
          <input name="navn" required className={inputKlasse} />
        </Field>
        <Field label="Zaptec Portal-brukernavn (e-post)">
          <input name="username" type="email" required className={inputKlasse} />
        </Field>
        <Field label="Zaptec Portal-passord">
          <input name="password" type="password" required className={inputKlasse} />
        </Field>
        <p className="text-xs text-slate-400">
          Innloggingen valideres mot Zaptec før den lagres, og lagres kryptert.
        </p>
        {feil && <p className="text-sm text-red-600">{feil}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={knappSekundær}>
            Avbryt
          </button>
          <button type="submit" disabled={jobber} className={knappPrimær}>
            {jobber ? 'Validerer …' : 'Lagre og synk'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Anlegg ----------

function Anlegg() {
  const qc = useQueryClient();
  const anleggQuery = useQuery<Installation[]>({
    queryKey: ['installations'],
    queryFn: () => api.get('/installations'),
  });
  const tarifferQuery = useQuery<GridTariff[]>({
    queryKey: ['grid-tariffs'],
    queryFn: () => api.get('/grid-tariffs'),
  });
  const [feil, setFeil] = useState<string | null>(null);

  const oppdater = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/installations/${id}`, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['installations'] }),
    onError: (err) => setFeil(err instanceof Error ? err.message : 'Feil'),
  });

  return (
    <div className="space-y-3">
      {feil && <p className="text-sm text-red-600">{feil}</p>}
      <Tabell hoder={['Navn', 'Adresse', 'Prisområde', 'Nettleie', 'Mva %', 'Aktiv']}>
        {(anleggQuery.data ?? []).map((a) => (
          <tr key={a.id} className="border-b border-slate-100 last:border-0">
            <td className="px-4 py-2 font-medium text-slate-700">{a.navn}</td>
            <td className="px-4 py-2 text-slate-500">{a.adresse ?? '–'}</td>
            <td className="px-4 py-2">
              <select
                value={a.prisomrade}
                onChange={(e) => oppdater.mutate({ id: a.id, body: { prisomrade: e.target.value } })}
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              >
                {['NO1', 'NO2', 'NO3', 'NO4', 'NO5'].map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </td>
            <td className="px-4 py-2">
              <select
                value={a.gridTariffId ?? ''}
                onChange={(e) =>
                  oppdater.mutate({ id: a.id, body: { gridTariffId: e.target.value || null } })
                }
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              >
                <option value="">– ingen –</option>
                {(tarifferQuery.data ?? []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.navn}
                  </option>
                ))}
              </select>
            </td>
            <td className="px-4 py-2">
              <input
                type="number"
                defaultValue={Number(a.mvaSats)}
                min={0}
                max={25}
                className="w-16 rounded border border-slate-300 px-2 py-1 text-sm"
                onBlur={(e) => {
                  const v = Number(e.target.value);
                  if (v !== Number(a.mvaSats)) oppdater.mutate({ id: a.id, body: { mvaSats: v } });
                }}
              />
            </td>
            <td className="px-4 py-2">{a.aktiv ? 'Ja' : 'Nei'}</td>
          </tr>
        ))}
      </Tabell>
      <p className="text-xs text-slate-400">
        Anlegg hentes automatisk fra leverandøren. Prisområde og nettleie må settes manuelt — de styrer
        kostnadsberegningen.
      </p>
    </div>
  );
}

// ---------- Nettleie ----------

function Nettleie() {
  const qc = useQueryClient();
  const [ny, setNy] = useState(false);
  const tarifferQuery = useQuery<GridTariff[]>({
    queryKey: ['grid-tariffs'],
    queryFn: () => api.get('/grid-tariffs'),
  });

  return (
    <div className="space-y-3">
      <button onClick={() => setNy(true)} className={knappPrimær}>
        Ny nettleiemodell
      </button>
      <Tabell hoder={['Navn', 'Energiledd dag', 'Energiledd natt/helg', 'Dagtid', 'Fastledd/mnd']}>
        {(tarifferQuery.data ?? []).map((t) => (
          <tr key={t.id} className="border-b border-slate-100 last:border-0">
            <td className="px-4 py-2 font-medium text-slate-700">{t.navn}</td>
            <td className="px-4 py-2">{Number(t.energileddDagOre).toFixed(1)} øre/kWh</td>
            <td className="px-4 py-2">{Number(t.energileddNattOre).toFixed(1)} øre/kWh</td>
            <td className="px-4 py-2">
              {String(t.dagFraTime).padStart(2, '0')}–{String(t.dagTilTime).padStart(2, '0')}
              {t.helgSomNatt ? ' (helg = natt)' : ''}
            </td>
            <td className="px-4 py-2">{Number(t.fastleddMnd).toFixed(0)} kr</td>
          </tr>
        ))}
      </Tabell>
      <p className="text-xs text-slate-400">Satser i øre/kWh inkl. mva, slik de står på nettleiefakturaen.</p>

      {ny && (
        <NettleieModal
          onClose={() => {
            setNy(false);
            qc.invalidateQueries({ queryKey: ['grid-tariffs'] });
          }}
        />
      )}
    </div>
  );
}

function NettleieModal({ onClose }: { onClose: () => void }) {
  const [feil, setFeil] = useState<string | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await api.post('/grid-tariffs', {
        navn: fd.get('navn'),
        energileddDagOre: Number(fd.get('dag')),
        energileddNattOre: Number(fd.get('natt')),
        dagFraTime: Number(fd.get('fra')),
        dagTilTime: Number(fd.get('til')),
        helgSomNatt: fd.get('helg') === 'on',
        fastleddMnd: Number(fd.get('fastledd') || 0),
      });
      onClose();
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    }
  }

  return (
    <Modal tittel="Ny nettleiemodell" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Navn (f.eks. «Glitre Nett husholdning»)">
          <input name="navn" required className={inputKlasse} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Energiledd dag (øre/kWh inkl. mva)">
            <input name="dag" type="number" step="0.1" required className={inputKlasse} />
          </Field>
          <Field label="Energiledd natt/helg (øre/kWh)">
            <input name="natt" type="number" step="0.1" required className={inputKlasse} />
          </Field>
          <Field label="Dag fra (time)">
            <input name="fra" type="number" defaultValue={6} min={0} max={23} className={inputKlasse} />
          </Field>
          <Field label="Dag til (time)">
            <input name="til" type="number" defaultValue={22} min={1} max={24} className={inputKlasse} />
          </Field>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input name="helg" type="checkbox" defaultChecked /> Helg regnes som natt
        </label>
        <Field label="Fastledd per måned (kr, vises kun som info)">
          <input name="fastledd" type="number" step="1" defaultValue={0} className={inputKlasse} />
        </Field>
        {feil && <p className="text-sm text-red-600">{feil}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={knappSekundær}>
            Avbryt
          </button>
          <button type="submit" className={knappPrimær}>
            Lagre
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Strømavtaler ----------

function Avtaler() {
  const qc = useQueryClient();
  const [ny, setNy] = useState(false);
  const avtalerQuery = useQuery<PowerDeal[]>({
    queryKey: ['power-deals'],
    queryFn: () => api.get('/power-deals'),
  });
  const personsQuery = useQuery<Person[]>({ queryKey: ['persons'], queryFn: () => api.get('/persons') });
  const grupperQuery = useQuery<EconomyGroup[]>({
    queryKey: ['economy-groups'],
    queryFn: () => api.get('/economy-groups'),
  });

  const personNavn = (id: string | null) => personsQuery.data?.find((p) => p.id === id)?.navn;
  const gruppeNavn = (id: string | null) => grupperQuery.data?.find((g) => g.id === id)?.navn;

  return (
    <div className="space-y-3">
      <button onClick={() => setNy(true)} className={knappPrimær}>
        Ny strømavtale
      </button>
      <Tabell hoder={['Gjelder', 'Type', 'Påslag', 'Fastpris', 'Gyldig fra', 'Gyldig til']}>
        {(avtalerQuery.data ?? []).map((a) => (
          <tr key={a.id} className="border-b border-slate-100 last:border-0">
            <td className="px-4 py-2 font-medium text-slate-700">
              {a.personId
                ? personNavn(a.personId)
                : a.economyGroupId
                  ? `Gruppe: ${gruppeNavn(a.economyGroupId)}`
                  : 'Hele anlegget (standard)'}
            </td>
            <td className="px-4 py-2">{a.type}</td>
            <td className="px-4 py-2">{Number(a.paslagOre).toFixed(1)} øre</td>
            <td className="px-4 py-2">{a.fastprisOre ? `${Number(a.fastprisOre).toFixed(0)} øre` : '–'}</td>
            <td className="px-4 py-2">{fmtDato(a.gyldigFra)}</td>
            <td className="px-4 py-2">{a.gyldigTil ? fmtDato(a.gyldigTil) : 'løpende'}</td>
          </tr>
        ))}
      </Tabell>
      <p className="text-xs text-slate-400">
        Rekkefølge: personlig avtale → økonomigruppens → hele anleggets standardavtale → ren spotpris.
        Har sameiet/husstanden én felles strømkontrakt, legg den inn som «Hele anlegget (standard)».
      </p>

      {ny && (
        <AvtaleModal
          personer={personsQuery.data ?? []}
          grupper={grupperQuery.data ?? []}
          onClose={() => {
            setNy(false);
            qc.invalidateQueries({ queryKey: ['power-deals'] });
          }}
        />
      )}
    </div>
  );
}

function AvtaleModal({
  personer,
  grupper,
  onClose,
}: {
  personer: Person[];
  grupper: EconomyGroup[];
  onClose: () => void;
}) {
  const [feil, setFeil] = useState<string | null>(null);
  const [type, setType] = useState('SPOT');
  const [gjelder, setGjelder] = useState('');

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const [slag, id] = gjelder.split(':');
    try {
      await api.post('/power-deals', {
        personId: slag === 'p' ? id : null,
        economyGroupId: slag === 'g' ? id : null,
        type,
        navn: fd.get('navn') || null,
        paslagOre: Number(fd.get('paslag') || 0),
        fastprisOre: fd.get('fastpris') ? Number(fd.get('fastpris')) : null,
        fastbelopMnd: Number(fd.get('fastbelop') || 0),
        gyldigFra: fd.get('fra'),
        gyldigTil: fd.get('til') || null,
      });
      onClose();
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    }
  }

  return (
    <Modal tittel="Ny strømavtale" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3">
        <Field label="Gjelder for">
          <select value={gjelder} onChange={(e) => setGjelder(e.target.value)} required className={inputKlasse}>
            <option value="">– velg –</option>
            <option value="t:">Hele anlegget (standard for alle)</option>
            <optgroup label="Personer">
              {personer.map((p) => (
                <option key={p.id} value={`p:${p.id}`}>
                  {p.navn}
                </option>
              ))}
            </optgroup>
            <optgroup label="Økonomigrupper">
              {grupper.map((g) => (
                <option key={g.id} value={`g:${g.id}`}>
                  {g.navn}
                </option>
              ))}
            </optgroup>
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Type">
            <select value={type} onChange={(e) => setType(e.target.value)} className={inputKlasse}>
              <option value="SPOT">Spot + påslag</option>
              <option value="FASTPRIS">Fastpris</option>
              <option value="NORGESPRIS">Norgespris</option>
            </select>
          </Field>
          <Field label="Avtalenavn (valgfritt)">
            <input name="navn" className={inputKlasse} />
          </Field>
          {type === 'SPOT' && (
            <Field label="Påslag (øre/kWh inkl. mva)">
              <input name="paslag" type="number" step="0.1" defaultValue={0} className={inputKlasse} />
            </Field>
          )}
          {type !== 'SPOT' && (
            <Field label={type === 'NORGESPRIS' ? 'Pris (øre/kWh, standard 50)' : 'Fastpris (øre/kWh inkl. mva)'}>
              <input
                name="fastpris"
                type="number"
                step="0.1"
                defaultValue={type === 'NORGESPRIS' ? 50 : undefined}
                className={inputKlasse}
              />
            </Field>
          )}
          <Field label="Fastbeløp per måned (kr)">
            <input name="fastbelop" type="number" step="1" defaultValue={0} className={inputKlasse} />
          </Field>
          <Field label="Gyldig fra">
            <input name="fra" type="date" required className={inputKlasse} />
          </Field>
          <Field label="Gyldig til (tomt = løpende)">
            <input name="til" type="date" className={inputKlasse} />
          </Field>
        </div>
        {feil && <p className="text-sm text-red-600">{feil}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={knappSekundær}>
            Avbryt
          </button>
          <button type="submit" className={knappPrimær}>
            Lagre
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- Zaptec-brukermapping ----------

interface UserMapRow {
  id: string;
  vendor: string;
  externalUserId: string;
  visningsnavn: string | null;
  personId: string;
}

function ZaptecBrukere() {
  const qc = useQueryClient();
  const mapQuery = useQuery<UserMapRow[]>({
    queryKey: ['vendor-user-map'],
    queryFn: () => api.get('/vendor-user-map'),
  });
  const personsQuery = useQuery<Person[]>({ queryKey: ['persons'], queryFn: () => api.get('/persons') });
  const [feil, setFeil] = useState<string | null>(null);

  const slett = useMutation({
    mutationFn: (id: string) => api.delete(`/vendor-user-map/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendor-user-map'] }),
  });

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await api.post('/vendor-user-map', {
        vendor: 'zaptec',
        externalUserId: fd.get('extId'),
        visningsnavn: fd.get('visningsnavn') || null,
        personId: fd.get('personId'),
      });
      (e.target as HTMLFormElement).reset();
      qc.invalidateQueries({ queryKey: ['vendor-user-map'] });
      setFeil(null);
    } catch (err) {
      setFeil(err instanceof Error ? err.message : 'Feil');
    }
  }

  const personNavn = (id: string) => personsQuery.data?.find((p) => p.id === id)?.navn ?? id;

  return (
    <div className="space-y-3">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
        <Field label="Zaptec bruker-ID">
          <input name="extId" required className={inputKlasse} />
        </Field>
        <Field label="Visningsnavn">
          <input name="visningsnavn" className={inputKlasse} />
        </Field>
        <Field label="Kobles til person">
          <select name="personId" required className={inputKlasse}>
            <option value="">– velg –</option>
            {(personsQuery.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.navn}
              </option>
            ))}
          </select>
        </Field>
        <button type="submit" className={knappPrimær}>
          Legg til
        </button>
      </form>
      {feil && <p className="text-sm text-red-600">{feil}</p>}
      <Tabell hoder={['Zaptec bruker-ID', 'Visningsnavn', 'Person', '']}>
        {(mapQuery.data ?? []).map((m) => (
          <tr key={m.id} className="border-b border-slate-100 last:border-0">
            <td className="px-4 py-2 font-mono text-xs">{m.externalUserId}</td>
            <td className="px-4 py-2">{m.visningsnavn ?? '–'}</td>
            <td className="px-4 py-2">{personNavn(m.personId)}</td>
            <td className="px-4 py-2 text-right">
              <button onClick={() => slett.mutate(m.id)} className="text-xs text-red-600 hover:underline">
                Fjern
              </button>
            </td>
          </tr>
        ))}
      </Tabell>
      <p className="text-xs text-slate-400">
        Brukes på delte anlegg (Zaptec Pro): økter med autentisert Zaptec-bruker tilordnes riktig person i
        stedet for laderens eier.
      </p>
    </div>
  );
}
